import { delistingReasonCategory } from '#delisting/constants'
import { contactTypeCategory, specificationCategory, targetSpeciesCategory, vesselGearCategory, vesselKindCategory, vesselRangeCategory, vesselTypeCategory } from '#details/constants';
import { useRecordCalls } from '#record/calls'
import { includesDelisted, PreFlight, QueryFieldProps } from '#search/model'
import { useAppSettings } from '#settings/settings';
import { useToggles } from '#toggle/provider'
import { trxAuthzTypeCategory } from '#trxauth/constants'
import { ircsscheme } from '#vid/constants'
import { useIOTCNumber } from '#vid/iotcnumber';
import { useVID } from '#vid/model';
import { InputNumber } from 'antd';
import { CategoryReference } from 'apprise-core/category';
import { TagReference } from 'apprise-core/tag';
import { Tenant } from 'apprise-core/tenant';
import { useT } from 'apprise-frontend-core/intl/language';
import { useOracle } from 'apprise-frontend-iam/authz/oracle';
import { useTenantModel } from 'apprise-frontend-iam/tenant/model';
import { useTenantStore } from 'apprise-frontend-iam/tenant/store';
import { MultiTenantBox } from 'apprise-frontend-iam/tenant/tenantbox';
import { TagLabel } from 'apprise-frontend-tags/tag/label';
import { useTagModel } from 'apprise-frontend-tags/tag/model';
import { useTagStore } from 'apprise-frontend-tags/tag/store';
import { useTagUtils } from 'apprise-frontend-tags/tag/utils';
import { Button } from 'apprise-ui/button/button';
import { classname } from 'apprise-ui/component/model';
import { DateBox } from 'apprise-ui/date/datebox';
import { MultiSelectBox, SelectBox } from 'apprise-ui/selectbox/selectbox';
import { TokenBox } from 'apprise-ui/tokenbox/tokenbox';
import { Fragment, useEffect, useState } from 'react';
import { MdOutlineMoreVert } from 'react-icons/md';
import "./fields.scss";



export const FlagField = (props: QueryFieldProps<{

    flags: TagReference[]

}>) => {

    const { children, label, onChange } = props

    const oracle = useOracle()
    const store = useTenantStore()
    const model = useTenantModel()
    const logged = useOracle()
    const excludeFlags = useAppSettings().excludedFlags ?? []

    const excludedButCurrent = excludeFlags.filter(t => t !== logged.tenant())

    const isIncluded = (t: Tenant) => logged.isTenantUser() ? !excludedButCurrent.includes(t.id) : true

    const tenants = (oracle.isGuest() || oracle.isTenantUser() ? store.allRefs() : store.all()).filter(isIncluded).sort(model.comparator)

    return <MultiTenantBox
        tenants={tenants}
        horizontal label={label} noClear placeholder={null} minWidth={200} onChange={flags => onChange?.(flags ? { flags } : undefined)}>
        {children?.flags}
    </MultiTenantBox>

}

export const NameField = (props: TokenizedFieldProps) => <TokenizedField {...props} field='names' />

export const TypeField = (props: TagFieldProps) => <TagField {...props} field='types' category={vesselTypeCategory} />

export const KindField = (props: TagFieldProps) => <TagField {...props} field='kinds' category={vesselKindCategory} />

export const GearField = (props: TagFieldProps) => <TagField {...props} field='gears' category={vesselGearCategory} />

export const RangeField = (props: TagFieldProps) => <TagField {...props} field='ranges' category={vesselRangeCategory} />

// the reasons a vessel was delisted for. only delisting records carry one, so the pill is for the searches that
// include them: historical ones, and current ones with the delisted toggle on.
export const DelistingReasonField = (props: TagFieldProps) => <TagField {...props} field='delistingReasons' category={delistingReasonCategory} />

export const PortField = (props: TokenizedFieldProps) => <TokenizedField {...props} field='ports' />

type TagFieldProps = QueryFieldProps

const TagField = (props: TagFieldProps & { field: string, category: CategoryReference, noLabel?: boolean }) => {

    const { label, children, field, category, onChange, noLabel, disabled } = props
    const model = useTagModel()

    const tags = useTagStore().allTagsOfCategory(category).sort(model.comparator)
    const textOf = useTagUtils().nameOf

    // a pill outside its mode is disabled: the box greys out and stops reporting changes.
    return <MultiSelectBox label={noLabel ? undefined : label} minWidth={200} horizontal disabled={disabled}

        options={tags.map(t => t.id)}
        render={t => <TagLabel bare noIcon tag={t} />}
        textOf={textOf}
        onChange={refs => onChange?.(refs ? { [field]: refs } : undefined)}>

        {children?.[field]}
    </MultiSelectBox>

}


type TokenizedFieldProps = QueryFieldProps

export const TokenizedField = (props: TokenizedFieldProps & { field: string }) => {

    const { children, field, label, onChange } = props

    return <TokenBox minWidth={200} label={label} horizontal noClear onChange={values => onChange?.(values ? { [field]: values } : undefined)}>
        {children?.[field]}
    </TokenBox>

}


type VidFieldProps = { vids: TagReference[], fuzzy?: boolean }
type IdFieldProps = { ids: TagReference[] }

export const VidField = (props: QueryFieldProps<VidFieldProps | IdFieldProps>
) => {

    const t = useT()


    const { allSchemes, parse, stringify } = useVID()

    const primaryScheme = useAppSettings().primaryScheme

    const vid = useIOTCNumber()

    const schemes = allSchemes(true)

    const fixedScheme = schemes.length === 1 && schemes[0]

    const schemeOptions = schemes.map(s => s.id)

    const { children, onChange } = props

    const vids = ((children as VidFieldProps)?.vids ?? (children as IdFieldProps)?.ids ?? []).map(parse)

    const values = vids.map(({ value }) => value!)

    const classes = classname('selectfield', fixedScheme && 'none')

    const [selectedScheme, setSelectedScheme] = useState(vids[0]?.scheme ?? primaryScheme ?? schemeOptions?.[0])

    // changes spec only if we have a min or max.
    const changeScheme = (scheme: TagReference | undefined) => {

        if (!scheme)
            return

        setSelectedScheme(scheme);

    }

    // rebase values on new scheme selection.
    useEffect(() =>

        changeVids(values),

        // eslint-disable-next-line
        [selectedScheme])

    const changeVids = (values: TagReference[] | undefined) => {

        if (!values || values.length === 0) {
            onChange?.(undefined)
            return
        }

        var vids = values.map(value =>

            selectedScheme === primaryScheme ? vid.postProcessValue(value) : value

        ).map(value => stringify(selectedScheme, value))


        // IRCS matches partially, the other schemes exactly.
        onChange?.(selectedScheme === primaryScheme ? { ids: vids } : selectedScheme === ircsscheme ? { vids, fuzzy: true } : { vids: vids })


    }

    return <div className={classes}>

        {!!fixedScheme ||

            <SelectBox noSearch className={classes} noReadonly noClear options={schemeOptions} render={s => <TagLabel noIcon bare tag={s} title={s === primaryScheme ? t('search.pill_primary_label') : undefined} />} onChange={changeScheme}>
                {selectedScheme}
            </SelectBox>
        }

        <TokenBox minWidth={200} noClear label={fixedScheme ? fixedScheme?.name : undefined} onChange={changeVids}>
            {values}
        </TokenBox>
    </div>
}

export const SpecField = (props: QueryFieldProps<Partial<{ spec: TagReference, min: number, max: number }>>) => {

    const t = useT()

    const tags = useTagStore()

    const specs = tags.allTagsOfCategory(specificationCategory)

    const fixedSpec = specs.length === 1 && specs[0]

    const { children, onChange } = props

    const specOptions = specs.map(s => s.id)

    const classes = classname('selectfield', fixedSpec && 'mono')

    const initialSpec = children?.spec ?? specs[0]?.id

    const [selectedSpec, setSelectedSpec] = useState(initialSpec)

    // changes spec only if we have a min or max.
    const changeSpec = (spec: TagReference | undefined) => {

        if (!spec)
            return

        setSelectedSpec(spec);

        if (children?.min || children?.max)
            onChange?.({ ...children, spec })

    }

    // change min/max with current spec, if we have still a value to preserve overall.
    const changeMin = (min: number | null) => onChange?.(min || children?.max ? { ...children, spec: selectedSpec, min: min ?? undefined } : undefined)
    const changeMax = (max: number | null) => onChange?.(max || children?.min ? { ...children, spec: selectedSpec, max: max ?? undefined } : undefined)

    return <div className={classes}>

        {!!fixedSpec ||

            <SelectBox noSearch className={classes} noClear options={specOptions} render={s => <TagLabel noIcon bare tag={s} />} onChange={changeSpec}>
                {selectedSpec}
            </SelectBox>
        }
        <InputNumber prefix={<span className='prefix'>{t('search.pill_min')}</span>} value={children?.min} controls={false} onChange={changeMin} />
        <InputNumber prefix={<span className='prefix'>{t('search.pill_max')}</span>} value={children?.max} controls={false} onChange={changeMax} />

    </div>

}

export const ContactField = (props: QueryFieldProps<{ fuzzy: boolean, type: TagReference, contacts: TagReference[] }>) => {

    const tags = useTagStore()

    const types = tags.allTagsOfCategory(contactTypeCategory)

    const fixedType = types.length === 1 && types[0]

    const { children, onChange } = props

    const typeOptions = types.map(s => s.id)

    const classes = classname('selectfield', fixedType && 'mono')

    const initialContact = children?.type ?? types[0]?.id

    const [selectedType, setSelectedType] = useState(initialContact)

    // changes type only if we have some contacts
    const changeType = (type: TagReference | undefined) => {

        if (!type)
            return

        setSelectedType(type);

        if (children?.contacts)
            onChange?.({ ...children, type })

    }

    // change contacts with current spec, if we have still a value to preserve overall.
    const changeContacts = (contacts: string[] | undefined) => onChange?.(contacts ? { fuzzy: true, type: selectedType, contacts } : undefined)

    return <div className={classes}>

        {!!fixedType ||

            <SelectBox noSearch className={classes} flexWidth noClear options={typeOptions} render={s => <TagLabel noIcon bare tag={s} />} onChange={changeType}>
                {selectedType}
            </SelectBox>
        }

        <TokenBox separators={[]} minWidth={200} noClear label={fixedType ? fixedType?.name : undefined} onChange={changeContacts}>
            {children?.contacts}
        </TokenBox>

    </div>

}

// the countries a tenant filter offers: for country users, all but the excluded flags other than their own;
// for guests and the secretariat, all of them.
const useSearchTenants = () => {

    const oracle = useOracle()
    const store = useTenantStore()
    const model = useTenantModel()
    const excludeFlags = useAppSettings().excludedFlags ?? []
    const excludedButCurrent = excludeFlags.filter(t => t !== oracle.tenant())
    const isIncluded = (t: Tenant) => oracle.isTenantUser() ? !excludedButCurrent.includes(t.id) : true

    return (oracle.isGuest() || oracle.isTenantUser() ? store.allRefs() : store.all()).filter(isIncluded).sort(model.comparator)
}

type CompoundFieldProps<T extends string> = {

    types: readonly T[]
    // the rows shown for a new filter.
    defaults: T[]
    // the current value, whose keys reopen a saved query with its rows.
    value: Partial<Record<T, unknown>> | undefined
    addTip: string
    fieldFor: (type: T) => JSX.Element
    // a row changes criterion: the field drops the old criterion's value, which the row no longer shows.
    onSwitch: (from: T, to: T) => void

}

// a filter made of several criteria, one row each: a criterion selector and its input.
// rows are added one at a time, and only the last one can change criterion.
// the wrapping field owns the value: each input writes its own key into it.
const CompoundField = <T extends string>(props: CompoundFieldProps<T>) => {

    const t = useT()

    const { types, defaults, value, addTip, fieldFor, onSwitch } = props

    // a value with no criteria left, e.g. all cleared and saved, reopens on the default rows.
    const initialFilters = value ? Object.keys(value) as T[] : []

    const [selectedFilters, selectedFiltersSet] = useState<T[]>(initialFilters.length ? initialFilters : defaults)

    const switchRow = (i: number, type: T) => {

        onSwitch(selectedFilters[i], type)
        selectedFiltersSet(selectedFilters.with(i, type))
    }

    const classes = classname('selectfield')

    const renderType = (type: T) => <span style={{ fontVariant: 'small-caps' }}>{t(`search.${type}`)}</span>

    const inputQueryFields = selectedFilters.map((fieldType, i) => {

        const availableSelected = selectedFilters.slice(0, i)
        const availableOptions = types.filter(t => !availableSelected.includes(t))

        return <Fragment key={i}>

            <div className={`${classes} filter-select`}>
                <SelectBox noSearch noClear disabled={i < selectedFilters.length - 1 || availableOptions.length === 1} flexWidth options={availableOptions} render={renderType} onChange={type => type && type !== fieldType && switchRow(i, type)}>
                    {fieldType}
                </SelectBox>
            </div>

            {fieldFor(fieldType)}

        </Fragment>
    })

    const onAdd = () => {

        const filtered = types.filter(t => !selectedFilters.includes(t))

        if (filtered.length)
            selectedFiltersSet([...selectedFilters, filtered[0]])
    }

    const disabled = selectedFilters.length === types.length

    return <Fragment>

        {inputQueryFields}

        {!disabled && <Button noLabel tip={t(addTip)} className='query-pill-selected-close filter-add' type='ghost' onClick={onAdd} icon={<MdOutlineMoreVert />} />}

    </Fragment>

}


const avlTypes = ['avl_active_year', 'avl_flagstate', 'avl_target_species', 'avl_charterer_name', 'avl_charterer_address'] as const

export type AvlTypeField = typeof avlTypes[number]

const activeYears = Array.from({ length: new Date().getFullYear() - 1998 }, (_, i) => `${new Date().getFullYear() - 1 - i}`)

export const AvlField = (props: QueryFieldProps<{ avl: Partial<Record<AvlTypeField, string[]>> }>) => {

    const { children, onChange } = props

    const tenants = useSearchTenants()
    const tagModel = useTagModel()

    const tags = useTagStore().allTagsOfCategory(targetSpeciesCategory).sort(tagModel.comparator)
    const textOf = useTagUtils().nameOf

    const avl = children?.avl

    const change = (type: AvlTypeField) => (refs: string[] | undefined) => onChange?.({ avl: { ...avl, [type]: refs } })

    const fieldFor = (type: AvlTypeField) => {

        switch (type) {

            case 'avl_target_species': return <MultiSelectBox minWidth={200} horizontal
                options={tags.map(t => t.id)}
                render={t => <TagLabel bare noIcon tag={t} />}
                textOf={textOf}
                onChange={change(type)}>
                {avl?.[type]}
            </MultiSelectBox>

            case 'avl_active_year': return <MultiSelectBox minWidth={130} horizontal
                options={activeYears}
                render={t => t}
                onChange={change(type)}>
                {avl?.[type]}
            </MultiSelectBox>

            case 'avl_charterer_name':
            case 'avl_charterer_address': return <TokenBox separators={[]} minWidth={130} noClear onChange={change(type)}>
                {avl?.[type]}
            </TokenBox>

            case 'avl_flagstate': return <MultiTenantBox
                tenants={tenants}
                horizontal noClear placeholder={null} minWidth={200}
                onChange={change(type)}>
                {avl?.[type]}
            </MultiTenantBox>
        }
    }

    return <CompoundField types={avlTypes} defaults={['avl_active_year', 'avl_flagstate']} value={avl} addTip='search.add_more_avl_filter' fieldFor={fieldFor} onSwitch={from => change(from)(undefined)} />

}


const cvTrxTypes = ['cv_trx_country', 'cv_trx_status', 'cv_trx_type', 'cv_trx_period'] as const

export type CvTrxTypeField = typeof cvTrxTypes[number]

// 'any' matches valid and expired authorisations alike: vessels that have or had one.
const cvTrxStatuses = ['any', 'current', 'expired'] as const

type CvTrx = Partial<{

    cv_trx_country: TagReference[]
    cv_trx_type: TagReference[]
    cv_trx_period: { from?: string, to?: string }
    cv_trx_status: typeof cvTrxStatuses[number]

}>

// ROP CV TRX authorisations, by authorising country, type, period, and status (any, current or expired).
// all criteria must hold on the same authorisation. the status compares with today, so it applies to current
// searches only: in historical ones it greys out and cvTrxPreFlight leaves it out of the search.
export const CvTrxAuthField = (props: QueryFieldProps<{ cv_trx: CvTrx }>) => {

    const t = useT()

    const { children, onChange, query } = props

    // historical searches test every past version, and an old one would match 'expired' for a vessel licensed today.
    const statusInert = query?.mode === 'historical'

    const tenants = useSearchTenants()
    const tagModel = useTagModel()

    const trxTypes = useTagStore().allTagsOfCategory(trxAuthzTypeCategory).sort(tagModel.comparator)
    const textOf = useTagUtils().nameOf

    const cvTrx = children?.cv_trx

    // an emptied criterion leaves the query: the inputs report [] or a boundless period, not undefined.
    const isEmpty = (value: unknown) => Array.isArray(value) ? !value.length : typeof value === 'object' && value !== null && !Object.values(value).some(v => v)

    const change = <K extends CvTrxTypeField>(type: K) => (value: CvTrx[K]) => onChange?.({ cv_trx: { ...cvTrx, [type]: isEmpty(value) ? undefined : value } })

    const fieldFor = (type: CvTrxTypeField) => {

        switch (type) {

            case 'cv_trx_country': return <MultiTenantBox
                tenants={tenants}
                horizontal noClear placeholder={null} minWidth={160}
                onChange={change(type)}>
                {cvTrx?.[type]}
            </MultiTenantBox>

            case 'cv_trx_type': return <MultiSelectBox minWidth={160} horizontal
                options={trxTypes.map(t => t.id)}
                render={t => <TagLabel bare noIcon tag={t} />}
                textOf={textOf}
                onChange={change(type)}>
                {cvTrx?.[type]}
            </MultiSelectBox>

            // the class greys the box, the prop stops it reporting changes: the value stays for current searches.
            // 'any' shows as the <any> entry of MissingField, same label and style.
            case 'cv_trx_status': return <SelectBox className={classname(statusInert && 'apprise-disabled')} disabled={statusInert} minWidth={130} horizontal noSearch
                options={[...cvTrxStatuses]}
                render={s => s === 'any' ? <span style={{ fontVariant: 'small-caps' }}>{t('search.missing___any__').toLowerCase()}</span> : t(`search.cv_trx_${s}`)}
                onChange={change(type)}>
                {cvTrx?.[type]}
            </SelectBox>

            // sized like the authorisation period filter.
            case 'cv_trx_period': return <div className='daterange-container'>
                <DateBox placeholder={t('search.pill_from')} horizontal onChange={from => change(type)({ ...cvTrx?.[type], from })}>
                    {cvTrx?.[type]?.from}
                </DateBox>
                <DateBox placeholder={t('search.pill_to')} horizontal onChange={to => change(type)({ ...cvTrx?.[type], to })}>
                    {cvTrx?.[type]?.to}
                </DateBox>
            </div>
        }
    }

    return <CompoundField types={cvTrxTypes} defaults={['cv_trx_country', 'cv_trx_status']} value={cvTrx} addTip='search.add_more_cv_trx_filter' fieldFor={fieldFor} onSwitch={from => change(from)(undefined)} />

}

// leaves the CV TRX status out of historical searches, where CvTrxAuthField greys it out. the query keeps it.
// the domain drops it there too, for clients other than this one.
export const cvTrxPreFlight: PreFlight = (conditions, query) =>

    query.mode === 'historical' ? conditions.map(({ cv_trx: { cv_trx_status, ...cv_trx } = {}, ...rest }) => ({ ...rest, cv_trx })) : conditions


const trxAuthTypes = ['prior_trx_period', 'prior_trx_entities', 'prior_trx_numbers'] as const
export type TrxAuthField = typeof trxAuthTypes[number]

export const PriorTrxAuthField = (props: TagFieldProps & QueryFieldProps<{ fuzzy: boolean, type: TagReference, contacts: TagReference[] }>) => {

    const t = useT()

    const { children, onChange } = props

    const [selectedType, setSelectedType] = useState<TrxAuthField>('prior_trx_period')

    const classes = classname('selectfield')

    // changes type only if we have some contacts
    const changeType = (type: TrxAuthField | undefined) => {

        if (!type)
            return

        setSelectedType(type);

        onChange?.({ [type]: undefined })

    }


    const inputQueryField = selectedType === 'prior_trx_period' ?

        <div className='daterange-container'>
            <DateBox placeholder={t('search.pill_from')} horizontal onChange={date => onChange?.({ [selectedType]: { ...children?.[selectedType], from: date } })}>
                {children?.[selectedType].from}
            </DateBox>
            <DateBox horizontal placeholder={t('search.pill_to')} onChange={date => onChange?.({ [selectedType]: { ...children?.[selectedType], to: date } })}>
                {children?.[selectedType].to}
            </DateBox>

        </div> :


        selectedType === 'prior_trx_entities' || selectedType === 'prior_trx_numbers' ?

            <TokenBox separators={[]} minWidth={200} noClear onChange={(values: string[] | undefined) => onChange?.(values ? { [selectedType]: values } : undefined)}>
                {children?.[selectedType]}
            </TokenBox>
            :
            <Fragment />


    const renderSelector = type => <span style={{ fontVariant: 'small-caps' }}>{t(`search.${type}`)}</span>

    return <Fragment>

        <div className={classes}>
            <SelectBox noSearch className={classes} flexWidth noClear options={[...trxAuthTypes]} render={renderSelector} onChange={changeType}>
                {selectedType}
            </SelectBox>
        </div>

        {inputQueryField}


    </Fragment>

}


export const AuthzField = (props: QueryFieldProps<{ date: string }>) => {

    const { children, label, onChange } = props

    return <DateBox label={label} horizontal onChange={date => onChange?.(date ? { date } : undefined)}>
        {children?.date}
    </DateBox>

}

export const AuthzRangeField = (props: QueryFieldProps<{ auth: { from?: string, to?: string } }>) => {

    const t = useT()

    const { children, label, onChange } = props

    return <div className='daterange-container'>
        <DateBox label={label} placeholder={t('search.pill_from')} horizontal onChange={date => onChange?.({ auth: { ...children?.auth, from: date } })}>
            {children?.auth.from}
        </DateBox>
        <DateBox horizontal placeholder={t('search.pill_to')} onChange={date => onChange?.({ auth: { ...children?.auth, to: date } })}>
            {children?.auth.to}
        </DateBox>

    </div>
}

// the statuses of a vessel authorization, as the domain's AuthzStatusCondition knows them.
export const authzStatusOptions = ['current', 'expired']

// filters vessels on whether their authorization is current or has expired.
// like the other multi-selects, an empty selection restricts nothing. nor do both statuses together, which is more
// than either one or the other: the domain also keeps the vessels with no authorization dates, which match neither.
export const AuthzStatusField = (props: QueryFieldProps<{ authzStatuses: string[] }>) => {

    const t = useT()

    const { children, label, onChange, disabled } = props

    // the box shows what render returns, textOf only drives its type-ahead.
    const nameOf = (s: string) => t(`search.authz_status_${s}`)

    return <MultiSelectBox horizontal placeholder={null} minWidth={120} noClear disabled={disabled}
        options={authzStatusOptions}
        label={label}
        textOf={nameOf}
        render={nameOf}
        onChange={authzStatuses => onChange?.(authzStatuses ? { authzStatuses } : undefined)}>
        {children?.authzStatuses}
    </MultiSelectBox>

}



export const options = [


    '__any__',
    'name',
    'imo', 'regno', 'ircs',
    'port',
    'type',
    'loa', 'gt', 'volume',
    'owner', 'owner_address',
    'operator', 'operator_address',
    'beneficial', 'beneficial_address',
    'company', 'company_address', 'company_regno',
    'gear',
    'auth_from', 'auth_to',
    'photo_starboard', 'photo_portside', 'photo_bow'

]

// lists first the fields some vessel of the tab misses, then those none does, greyed but still selectable (#627).
// the counts span the whole tab and delisted setting of the edited query, not its other filters.
// while they load, or if they fail, the fields keep their usual order.
// toggled ('missing-counts'): off, no counts are asked and the order never changes. not an effect dep, so a toggle
// released from the Dev settings shows on the filter's next mount.
export const MissingField = (props: QueryFieldProps<{ missing_fields: string[] }>) => {

    const t = useT()

    const calls = useRecordCalls()

    const ranked = useToggles().isFeatureEnabled('missing-counts')

    const { children, label, onChange, disabled, query } = props

    const scope = query?.scope ?? 'rav'
    const includeDelisted = query ? includesDelisted(query) : false

    const [counts, setCounts] = useState<Record<string, number>>()

    useEffect(() => {

        // inert in historical mode: nothing to rank.
        if (disabled || !ranked)
            return

        // an answer that arrives after a change of tab or setting is stale.
        let live = true

        calls.missingCounts(scope, includeDelisted)
            .then(counts => live && setCounts(counts))
            .catch(() => live && setCounts(undefined))

        return () => { live = false }

        // eslint-disable-next-line
    }, [scope, includeDelisted, disabled])

    const hasNone = (f: string) => counts?.[f] === 0

    const ordered = [...options.filter(f => !hasNone(f)), ...options.filter(hasNone)]

    return <MultiSelectBox className='missing-field' horizontal placeholder={null} minWidth={300} noClear disabled={disabled}
        options={ordered}
        label={label}
        textOf={f => t(`search.missing_${f}`)}
        render={f => <span className={classname(hasNone(f) && 'missing-nomatch')} style={{ fontVariant: 'small-caps' }}>{t(`search.missing_${f}`).toLowerCase()}</span>}
        onChange={missing_fields => onChange?.(missing_fields ? { missing_fields } : undefined)}>
        {children?.missing_fields}
    </MultiSelectBox>

}